import React, { useEffect, useMemo, useState } from "react";
import { Card, CardBody, Modal, ModalBody, ModalHeader } from "reactstrap";
import { useNavigate } from "react-router-dom";
import { getAllReports } from "../../../redux/service/action";
import CommonPagination from "../../Common/Pagination/CommonPagination";
import FullScreenLoader from "../../Layouts/FullScreenLoader/FullScreenLoader";
import { formatDateTime } from "../../Common/Utils/dateUtils";
import { formatPurchaseSourceLabel, getMembershipTermLabel } from "../../Common/Utils/adminViewUtils";
import TestingAccountFilter from "../../Common/TestingAccountFilter";
import "../../Common/Styles/AdminEnhancements.css";

const INITIAL_PARAMS = {
  type: "payment_logs_report",
  page: 1,
  per_page: 10,
  search_key: "",
  status: "",
  purchase_source: "",
  payment_type: "",
  start_date: "",
  end_date: "",
  testing_account_filter: "hide",
};

const INITIAL_UNMATCHED_PARAMS = {
  page: 1,
  per_page: 5,
};

const STATUS_OPTIONS = [
  { value: "", label: "All Statuses" },
  { value: "1", label: "Completed" },
  { value: "0", label: "Pending" },
  { value: "2", label: "Failed" },
];

const SOURCE_OPTIONS = [
  { value: "", label: "All Sources" },
  { value: "website", label: "Website" },
  { value: "shopify", label: "Shopify" },
  { value: "wordpress", label: "WordPress" },
  { value: "unknown", label: "Unknown" },
];

const PAYMENT_TYPE_OPTIONS = [
  { value: "", label: "All Payment Types" },
  { value: "1", label: "Membership" },
  { value: "2", label: "Add-on" },
];

const getStatusClass = (status) => {
  if (Number(status) === 1) return "success";
  if (Number(status) === 2) return "danger";
  return "warning";
};

const getDirectionClass = (direction) => {
  if (direction === "outbound") return "info";
  if (direction === "inbound") return "success";
  return "warning";
};

const formatAmount = (value) => {
  const parsed = Number(value ?? 0);
  if (Number.isNaN(parsed)) return "--";
  return parsed.toFixed(2);
};

const normalizePrettyJson = (value) => {
  if (value === null || value === undefined || value === "") return "--";
  if (typeof value === "object") return JSON.stringify(value, null, 2);
  const text = String(value);
  try {
    return JSON.stringify(JSON.parse(text), null, 2);
  } catch (error) {
    return text;
  }
};

const getTimelineSourceLabel = (source) => {
  if (source === "persisted") return "Persisted timeline";
  if (source === "legacy_fallback") return "Legacy snapshot";
  if (source === "frontend_legacy_fallback") return "Legacy snapshot";
  return "No timeline";
};

const buildRowSubject = (row) => {
  const primary = row?.action_title || (Number(row?.payment_type) === 2 ? "Add-on payment event" : "Membership payment event");
  const membershipTitle = row?.membership_title || "Unknown membership";
  const sourceLabel = formatPurchaseSourceLabel(row?.purchase_source_label) || "Unknown source";
  return `${primary} | ${membershipTitle} | ${sourceLabel}`;
};

const buildLegacyTimelineEventsFromRow = (row) => {
  if (!row) return [];
  const events = [];

  if (row?.first_api_post) {
    events.push({
      event_id: null,
      direction: "outbound",
      direction_label: "Outbound",
      event_type: "legacy_first_api_request",
      event_type_label: "Legacy Outbound Request",
      status_label: "legacy",
      created_on: row?.created_on || null,
      endpoint: "",
      payload: row.first_api_post,
      payload_preview: row.first_api_post_preview || "--",
      related_invoice_id: row?.invoice_id || "--",
      related_recurring_id: "--",
      action_title: "Payment Initiated",
      action_hint: "Built from list payload fallback",
    });
  }

  if (row?.first_api_res) {
    events.push({
      event_id: null,
      direction: "inbound",
      direction_label: "Inbound",
      event_type: "legacy_first_api_response",
      event_type_label: "Legacy Gateway Reply",
      status_label: "legacy",
      created_on: row?.updated_on || row?.created_on || null,
      endpoint: "",
      payload: row.first_api_res,
      payload_preview: row.first_api_res_preview || "--",
      related_invoice_id: row?.invoice_id || "--",
      related_recurring_id: "--",
      action_title: row?.action_title || "Gateway Reply",
      action_hint: "Built from list payload fallback",
    });
  }

  const inboundPayload = row?.second_api_res || row?.second_api_post || row?.payment_response_json || "";
  if (inboundPayload) {
    events.push({
      event_id: null,
      direction: "inbound",
      direction_label: "Inbound",
      event_type: "legacy_inbound_callback",
      event_type_label: "Legacy Callback/Webhook",
      status_label: "legacy",
      created_on: row?.updated_on || row?.created_on || null,
      endpoint: "",
      payload: inboundPayload,
      payload_preview: row?.inbound_preview || row?.second_api_res_preview || row?.second_api_post_preview || row?.payment_response_preview || "--",
      related_invoice_id: row?.invoice_id || "--",
      related_recurring_id: "--",
      action_title: row?.action_title || "Webhook/callback received",
      action_hint: "Built from list payload fallback",
    });
  }

  return events;
};

const PaymentLogsReport = () => {
  const navigate = useNavigate();
  const [params, setParams] = useState(INITIAL_PARAMS);
  const [unmatchedParams, setUnmatchedParams] = useState(INITIAL_UNMATCHED_PARAMS);
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);
  const [unmatchedLoading, setUnmatchedLoading] = useState(false);
  const [timelineViewer, setTimelineViewer] = useState({
    isOpen: false,
    loading: false,
    title: "",
    context: {},
    events: [],
  });
  const [logViewer, setLogViewer] = useState({
    isOpen: false,
    title: "",
    content: "",
  });
  const [expandedRows, setExpandedRows] = useState({});
  const [reportsData, setReportsData] = useState({
    payment_logs_report: [],
    pagination_meta: null,
    event_logs_degraded: false,
    event_logs_degraded_reason: "",
  });
  const [unmatchedData, setUnmatchedData] = useState({
    unmatched_webhook_report: [],
    pagination_meta: null,
  });

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setParams((prev) => ({
        ...prev,
        search_key: searchText,
        page: 1,
      }));
      setUnmatchedParams((prev) => ({
        ...prev,
        page: 1,
      }));
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchText]);

  useEffect(() => {
    let active = true;

    const fetchReport = async () => {
      setLoading(true);
      try {
        const response = await getAllReports(params);
        if (!active) return;

        if (response?.data?.status) {
          const payload = response?.data?.response || {};
          setReportsData({
            payment_logs_report: payload?.payment_logs_report || [],
            pagination_meta: payload?.pagination_meta || null,
            event_logs_degraded: Boolean(payload?.event_logs_degraded),
            event_logs_degraded_reason: payload?.event_logs_degraded_reason || "",
          });
        } else {
          setReportsData({
            payment_logs_report: [],
            pagination_meta: null,
            event_logs_degraded: false,
            event_logs_degraded_reason: "",
          });
        }
      } catch (error) {
        if (active) {
          console.error(error);
          setReportsData({
            payment_logs_report: [],
            pagination_meta: null,
            event_logs_degraded: false,
            event_logs_degraded_reason: "",
          });
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    fetchReport();
    return () => {
      active = false;
    };
  }, [params]);

  useEffect(() => {
    let active = true;

    const fetchUnmatched = async () => {
      setUnmatchedLoading(true);
      try {
        const response = await getAllReports({
          type: "unmatched_webhook_report",
          page: unmatchedParams.page,
          per_page: unmatchedParams.per_page,
          search_key: params.search_key,
          start_date: params.start_date,
          end_date: params.end_date,
        });
        if (!active) return;

        if (response?.data?.status) {
          const payload = response?.data?.response || {};
          setUnmatchedData({
            unmatched_webhook_report: payload?.unmatched_webhook_report || [],
            pagination_meta: payload?.pagination_meta || null,
          });
        } else {
          setUnmatchedData({
            unmatched_webhook_report: [],
            pagination_meta: null,
          });
        }
      } catch (error) {
        if (active) {
          console.error(error);
          setUnmatchedData({
            unmatched_webhook_report: [],
            pagination_meta: null,
          });
        }
      } finally {
        if (active) setUnmatchedLoading(false);
      }
    };

    fetchUnmatched();
    return () => {
      active = false;
    };
  }, [unmatchedParams, params.search_key, params.start_date, params.end_date]);

  const rows = reportsData?.payment_logs_report || [];
  const unmatchedRows = unmatchedData?.unmatched_webhook_report || [];

  const summary = useMemo(() => {
    const completed = rows.filter((row) => Number(row?.status) === 1).length;
    const failed = rows.filter((row) => Number(row?.status) === 2).length;
    const webhookRows = rows.filter((row) => Boolean(row?.webhook_received)).length;
    const timelineRows = rows.filter((row) => Boolean(row?.timeline_available)).length;

    return [
      {
        label: "Logs on this page",
        value: rows.length,
        subtext: reportsData?.pagination_meta?.total
          ? `${reportsData.pagination_meta.total} total logs`
          : "Current result set",
      },
      {
        label: "Completed",
        value: completed,
        subtext: "Payment status completed",
      },
      {
        label: "Failed",
        value: failed,
        subtext: "Payment status failed",
      },
      {
        label: "Webhook received",
        value: webhookRows,
        subtext: "Rows with callback/webhook payload",
      },
      {
        label: "Timeline available",
        value: timelineRows,
        subtext: "Rows with persisted timeline or legacy snapshots",
      },
      {
        label: "Unmatched webhooks",
        value: unmatchedData?.pagination_meta?.total || unmatchedRows.length,
        subtext: "Webhook events without transaction match",
      },
    ];
  }, [rows, reportsData, unmatchedData, unmatchedRows]);

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setParams((prev) => ({
      ...prev,
      [name]: value,
      page: 1,
    }));
    setUnmatchedParams((prev) => ({
      ...prev,
      page: 1,
    }));
  };

  const resetFilters = () => {
    setSearchText("");
    setParams(INITIAL_PARAMS);
    setUnmatchedParams(INITIAL_UNMATCHED_PARAMS);
  };

  const openLogViewer = (title, content) => {
    setLogViewer({
      isOpen: true,
      title,
      content: normalizePrettyJson(content),
    });
  };

  const closeLogViewer = () => {
    setLogViewer({
      isOpen: false,
      title: "",
      content: "",
    });
  };

  const closeTimelineViewer = () => {
    setTimelineViewer({
      isOpen: false,
      loading: false,
      title: "",
      context: {},
      events: [],
    });
  };

  const toggleRowExpanded = (rowKey) => {
    setExpandedRows((prev) => ({
      ...prev,
      [rowKey]: !prev[rowKey],
    }));
  };

  const openTimelineViewer = async ({ title, transactionId, eventGroupKey, row }) => {
    setTimelineViewer({
      isOpen: true,
      loading: true,
      title,
      context: {},
      events: [],
    });

    try {
      const payload = {
        type: "payment_log_timeline",
      };
      if (transactionId) payload.transaction_id = transactionId;
      if (!transactionId && eventGroupKey) payload.event_group_key = eventGroupKey;

      const response = await getAllReports(payload);
      let context = {};
      let events = [];
      let eventLogsDegraded = false;
      let eventLogsDegradedReason = "";

      if (response?.data?.status) {
        const timelinePayload = response?.data?.response || {};
        context = (timelinePayload?.context && typeof timelinePayload.context === "object") ? timelinePayload.context : {};
        events = Array.isArray(timelinePayload?.timeline_events) ? timelinePayload.timeline_events : [];
        eventLogsDegraded = Boolean(timelinePayload?.event_logs_degraded || context?.event_logs_degraded);
        eventLogsDegradedReason = timelinePayload?.event_logs_degraded_reason || context?.event_logs_degraded_reason || "";
      }

      if (events.length === 0 && row) {
        const fallbackEvents = buildLegacyTimelineEventsFromRow(row);
        if (fallbackEvents.length > 0) {
          events = fallbackEvents;
          context = {
            ...context,
            transaction_id: context?.transaction_id || row?.transaction_id,
            event_group_key: context?.event_group_key || row?.event_group_key || `txn:${row?.transaction_id || "--"}`,
            timeline_available: true,
            timeline_source: context?.timeline_source || "frontend_legacy_fallback",
            timeline_note:
              context?.timeline_note ||
              "Showing legacy snapshots from row payload because no persisted timeline events were returned.",
          };
        }
      }

      context = {
        ...(context || {}),
        event_logs_degraded: eventLogsDegraded,
        event_logs_degraded_reason: eventLogsDegradedReason,
      };

      setTimelineViewer({
        isOpen: true,
        loading: false,
        title,
        context,
        events,
      });
    } catch (error) {
      console.error(error);
      setTimelineViewer({
        isOpen: true,
        loading: false,
        title,
        context: {},
        events: [],
      });
    }
  };

  const timelineEmptyMessage = timelineViewer?.context?.event_logs_degraded
    ? `Timeline persistence is currently unavailable${
        timelineViewer?.context?.event_logs_degraded_reason ? ` (${timelineViewer.context.event_logs_degraded_reason})` : ""
      }. Legacy snapshots may still appear when available.`
    : timelineViewer?.context?.timeline_source === "legacy_fallback" || timelineViewer?.context?.timeline_source === "frontend_legacy_fallback"
      ? "No persisted timeline events were found. This row uses legacy snapshots."
      : "No timeline events found for this item. This can happen for older legacy rows before timeline persistence was introduced.";

  return (
    <Card className="py-3">
      <div className="px-3">
        <div className="admin-toolbar">
          <div>
            <h4 className="mb-1">Payments Logs</h4>
            <div className="admin-toolbar-note">
              Track outbound requests to MyFatoorah, gateway replies, inbound callback/webhook payloads, and resulting
              system actions.
            </div>
          </div>
        </div>

        <div className="row mb-2">
          <div className="col-md-4 form-group">
            <label>Search</label>
            <input
              type="text"
              className="form-control"
              placeholder="Company, name, email, mobile, user id, transaction id, invoice id"
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
            />
          </div>
          <div className="col-md-2 form-group">
            <label>Status</label>
            <select className="form-control" name="status" value={params.status} onChange={handleFilterChange}>
              {STATUS_OPTIONS.map((option) => (
                <option key={option.value || "all"} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-2 form-group">
            <label>Source</label>
            <select className="form-control" name="purchase_source" value={params.purchase_source} onChange={handleFilterChange}>
              {SOURCE_OPTIONS.map((option) => (
                <option key={option.value || "all"} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-2 form-group">
            <label>Type</label>
            <select className="form-control" name="payment_type" value={params.payment_type} onChange={handleFilterChange}>
              {PAYMENT_TYPE_OPTIONS.map((option) => (
                <option key={option.value || "all"} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <TestingAccountFilter
            className="col-md-2 form-group"
            value={params.testing_account_filter}
            onChange={handleFilterChange}
          />
          <div className="col-md-2 form-group d-flex align-items-end">
            <button type="button" className="submit-btn" style={{ width: "100%" }} onClick={resetFilters}>
              Reset
            </button>
          </div>
          <div className="col-md-2 form-group">
            <label>From Date</label>
            <input type="date" className="form-control" name="start_date" value={params.start_date} onChange={handleFilterChange} />
          </div>
          <div className="col-md-2 form-group">
            <label>To Date</label>
            <input
              type="date"
              className="form-control"
              name="end_date"
              value={params.end_date}
              min={params.start_date || undefined}
              onChange={handleFilterChange}
            />
          </div>
        </div>

        <div className="admin-summary-grid">
          {summary.map((item) => (
            <div className="admin-summary-card" key={item.label}>
              <div className="admin-summary-label">{item.label}</div>
              <div className="admin-summary-value">{item.value}</div>
              <div className="admin-summary-subtext">{item.subtext}</div>
            </div>
          ))}
        </div>
      </div>

      {loading ? (
        <FullScreenLoader />
      ) : (
        <CardBody className="table-sec px-3">
          {reportsData?.event_logs_degraded ? (
            <div className="admin-highlight-box mb-3">
              Timeline persistence is currently degraded.
              {reportsData?.event_logs_degraded_reason ? ` Reason: ${reportsData.event_logs_degraded_reason}.` : ""}
              List rows remain available and legacy snapshots will be used where possible.
            </div>
          ) : null}
          <div className="customer360-table-wrap">
            <table className="table border-table-sec">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Subject</th>
                  <th>Created</th>
                  <th>Customer</th>
                  <th>Source</th>
                  <th>Payment Status</th>
                  <th>
                    Business Action
                    <div className="admin-inline-note">What this row means</div>
                  </th>
                  <th>Timeline</th>
                  <th>Details</th>
                  <th className="text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.length > 0 ? (
                  rows.map((row, index) => {
                    const pageOffset =
                      ((reportsData?.pagination_meta?.current_page || params.page || 1) - 1) *
                      (reportsData?.pagination_meta?.per_page || params.per_page || 10);
                    const rowKey = row?.transaction_id ? `txn-${row.transaction_id}` : `row-${index}`;
                    const isExpanded = Boolean(expandedRows[rowKey]);
                    return (
                      <React.Fragment key={rowKey}>
                        <tr className="admin-summary-row">
                          <td>{pageOffset + index + 1}</td>
                          <td>
                            <div>{buildRowSubject(row)}</div>
                            <div className="admin-inline-note">
                              Txn: {row?.transaction_id || "--"} | Invoice: {row?.invoice_id || "--"}
                            </div>
                          </td>
                          <td>{formatDateTime(row?.created_on)}</td>
                          <td>
                            <div>{row?.company_name || "--"}</div>
                            <div className="admin-inline-note">{row?.customer_name || "--"}</div>
                            <div className="admin-inline-note">User ID: {row?.user_id || "--"}</div>
                          </td>
                          <td>
                            <span className="admin-chip">{formatPurchaseSourceLabel(row?.purchase_source_label)}</span>
                          </td>
                          <td>
                            <span className={`admin-chip admin-chip--${getStatusClass(row?.status)}`}>{row?.status_label || "--"}</span>
                            <div className="admin-inline-note mt-1">{row?.reaction_label || "--"}</div>
                          </td>
                          <td>
                            <span className="admin-chip admin-chip--info">{row?.action_title || "--"}</span>
                            <div className="admin-inline-note mt-1">{row?.action_hint || "Derived from timeline and payload"}</div>
                          </td>
                          <td>
                            <div className="admin-inline-note">{row?.timeline_available ? `${row?.event_count || 0} events` : "No events"}</div>
                            <div className="admin-inline-note">{getTimelineSourceLabel(row?.timeline_source)}</div>
                            <div className="admin-inline-note">{row?.last_event_type_label || "--"}</div>
                            <button
                              type="button"
                              className="border-button mt-2"
                              onClick={() =>
                                openTimelineViewer({
                                  title: `Timeline - Txn ${row?.transaction_id || "--"}`,
                                  transactionId: row?.transaction_id,
                                  row,
                                })
                              }
                            >
                              <span>View Timeline</span>
                            </button>
                          </td>
                          <td>
                            <button type="button" className="border-button" onClick={() => toggleRowExpanded(rowKey)}>
                              <span>{isExpanded ? "Hide Details" : "View Details"}</span>
                            </button>
                          </td>
                          <td>
                            <div className="d-flex justify-content-center">
                              {row?.user_id ? (
                                <button className="border-button" onClick={() => navigate(`/user/update/${row?.user_id}`)}>
                                  <span>Update Customer</span>
                                </button>
                              ) : (
                                <span className="admin-inline-note">--</span>
                              )}
                            </div>
                          </td>
                        </tr>
                        {isExpanded ? (
                          <tr className="admin-details-row">
                            <td colSpan="10">
                              <div className="admin-details-panel">
                                <div className="row">
                                  <div className="col-lg-4 mb-3">
                                    <div className="admin-details-panel-title">Payment Context</div>
                                    <div>{row?.membership_title || "--"}</div>
                                    <div className="admin-inline-note">
                                      Type: {row?.payment_type === 2 ? "Add-on" : "Membership"} | Term: {getMembershipTermLabel(row) || "--"}
                                    </div>
                                    <div className="admin-inline-note">Amount: {formatAmount(row?.amount)}</div>
                                    <div className="admin-inline-note">Created: {formatDateTime(row?.created_on)}</div>
                                    <div className="admin-inline-note">Updated: {formatDateTime(row?.updated_on)}</div>
                                    <div className="admin-inline-note">Source: {formatPurchaseSourceLabel(row?.purchase_source_label)}</div>
                                    <div className="admin-inline-note">Store: {row?.store_name || "--"}</div>
                                  </div>
                                  <div className="col-lg-4 mb-3">
                                    <div className="admin-details-panel-title">
                                      Outbound to MyFatoorah
                                      <div className="admin-inline-note">Request sent by us</div>
                                    </div>
                                    <div style={{ maxWidth: "100%", whiteSpace: "normal" }}>
                                      {row?.outbound_preview || row?.first_api_post_preview || "--"}
                                    </div>
                                    {row?.first_api_post ? (
                                      <button
                                        type="button"
                                        className="border-button mt-2"
                                        onClick={() => openLogViewer("Outbound to MyFatoorah (Full)", row.first_api_post)}
                                      >
                                        <span>View Full</span>
                                      </button>
                                    ) : null}
                                  </div>
                                  <div className="col-lg-4 mb-3">
                                    <div className="admin-details-panel-title">
                                      MyFatoorah Reply
                                      <div className="admin-inline-note">Immediate API response</div>
                                    </div>
                                    <div style={{ maxWidth: "100%", whiteSpace: "normal" }}>
                                      {row?.gateway_reply_preview || row?.first_api_res_preview || "--"}
                                    </div>
                                    {row?.first_api_res ? (
                                      <button
                                        type="button"
                                        className="border-button mt-2"
                                        onClick={() => openLogViewer("MyFatoorah Reply (Full)", row.first_api_res)}
                                      >
                                        <span>View Full</span>
                                      </button>
                                    ) : null}
                                  </div>
                                  <div className="col-lg-4 mb-3">
                                    <div className="admin-details-panel-title">
                                      Inbound Callback/Webhook
                                      <div className="admin-inline-note">Payload received by us</div>
                                    </div>
                                    <div style={{ maxWidth: "100%", whiteSpace: "normal" }}>
                                      {row?.inbound_preview || row?.second_api_res_preview || row?.second_api_post_preview || row?.payment_response_preview || "--"}
                                    </div>
                                    <div className="admin-inline-note">{row?.webhook_received ? "Webhook/callback recorded" : "No webhook recorded"}</div>
                                    {row?.second_api_res || row?.second_api_post || row?.payment_response_json ? (
                                      <button
                                        type="button"
                                        className="border-button mt-2"
                                        onClick={() =>
                                          openLogViewer(
                                            "Inbound Callback/Webhook (Full)",
                                            row.second_api_res || row.second_api_post || row.payment_response_json
                                          )
                                        }
                                      >
                                        <span>View Full</span>
                                      </button>
                                    ) : null}
                                  </div>
                                  <div className="col-lg-4 mb-3">
                                    <div className="admin-details-panel-title">System Reaction</div>
                                    <div className="admin-inline-note">Business Action: {row?.action_title || "--"}</div>
                                    <div className="admin-inline-note">Action Hint: {row?.action_hint || "--"}</div>
                                    <div className="admin-inline-note">Reaction: {row?.reaction_label || "--"}</div>
                                    <div className="admin-inline-note">Stage: {row?.flow_stage || "--"}</div>
                                    <div className="admin-inline-note">Last Event: {row?.last_event_type_label || "--"}</div>
                                    <div className="admin-inline-note">Last Event At: {formatDateTime(row?.last_event_at)}</div>
                                  </div>
                                  <div className="col-lg-4 mb-3">
                                    <div className="admin-details-panel-title">Identifiers</div>
                                    <div className="admin-inline-note">Transaction ID: {row?.transaction_id || "--"}</div>
                                    <div className="admin-inline-note">Invoice ID: {row?.invoice_id || "--"}</div>
                                    <div className="admin-inline-note">Event Group: {row?.event_group_key || "--"}</div>
                                    <div className="admin-inline-note">Membership ID: {row?.membership_id || "--"}</div>
                                    <div className="admin-inline-note">Email: {row?.email_id || "--"}</div>
                                    <div className="admin-inline-note">
                                      Mobile: {`${row?.country_code || ""} ${row?.mobile_number || ""}`.trim() || "--"}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        ) : null}
                      </React.Fragment>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="10" className="text-center">
                      Payment logs were not found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <CommonPagination params={params} setParams={setParams} pagination_meta={reportsData?.pagination_meta} />

          <div className="admin-section-card mt-4">
            <div className="admin-section-title">
              <h5 className="mb-0">Unmatched Webhooks</h5>
              <span className="admin-chip admin-chip--warning">
                {unmatchedData?.pagination_meta?.total || unmatchedRows.length} events
              </span>
            </div>
            <p className="admin-section-note">
              Webhook/callback payloads that were received but did not match any transaction.
            </p>
            {unmatchedLoading ? (
              <FullScreenLoader />
            ) : (
              <>
                <div className="customer360-table-wrap">
                  <table className="table border-table-sec">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Created</th>
                        <th>Source</th>
                        <th>Identifiers</th>
                        <th>Event</th>
                        <th>Payload</th>
                        <th>Timeline</th>
                      </tr>
                    </thead>
                    <tbody>
                      {unmatchedRows.length > 0 ? (
                        unmatchedRows.map((row, index) => {
                          const pageOffset =
                            ((unmatchedData?.pagination_meta?.current_page || unmatchedParams.page || 1) - 1) *
                            (unmatchedData?.pagination_meta?.per_page || unmatchedParams.per_page || 5);
                          return (
                            <tr key={row?.event_id || `${row?.event_group_key}-${index}`}>
                              <td>{pageOffset + index + 1}</td>
                              <td>{formatDateTime(row?.created_on)}</td>
                              <td>
                                <span className="admin-chip">{formatPurchaseSourceLabel(row?.source_channel || "Unknown")}</span>
                                <div className="admin-inline-note">{row?.direction_label || "--"}</div>
                              </td>
                              <td>
                                <div className="admin-inline-note">Group: {row?.event_group_key || "--"}</div>
                                <div className="admin-inline-note">Invoice: {row?.related_invoice_id || "--"}</div>
                                <div className="admin-inline-note">Recurring: {row?.related_recurring_id || "--"}</div>
                              </td>
                              <td>
                                <div>{row?.event_type_label || "--"}</div>
                                <div className="admin-inline-note">{row?.action_title || "--"}</div>
                                <div className="admin-inline-note">{row?.action_hint || "--"}</div>
                                <div className="admin-inline-note">
                                  <span className={`admin-chip admin-chip--${getDirectionClass(row?.direction)}`}>{row?.status_label || "unmatched"}</span>
                                </div>
                              </td>
                              <td>
                                <div style={{ maxWidth: 320, whiteSpace: "normal" }}>{row?.payload_preview || "--"}</div>
                                {row?.payload ? (
                                  <button
                                    type="button"
                                    className="border-button mt-2"
                                    onClick={() => openLogViewer("Unmatched Webhook Payload (Full)", row.payload)}
                                  >
                                    <span>View Full</span>
                                  </button>
                                ) : null}
                              </td>
                              <td>
                                <button
                                  type="button"
                                  className="border-button"
                                  onClick={() =>
                                    openTimelineViewer({
                                      title: `Timeline - ${row?.event_group_key || `Event ${row?.event_id}`}`,
                                      eventGroupKey: row?.event_group_key || `event:${row?.event_id}`,
                                    })
                                  }
                                >
                                  <span>View Timeline</span>
                                </button>
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan="7" className="text-center">
                            No unmatched webhooks found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <CommonPagination
                  params={unmatchedParams}
                  setParams={setUnmatchedParams}
                  pagination_meta={unmatchedData?.pagination_meta}
                />
              </>
            )}
          </div>
        </CardBody>
      )}

      <Modal isOpen={timelineViewer.isOpen} toggle={closeTimelineViewer} size="xl" centered>
        <ModalHeader toggle={closeTimelineViewer}>{timelineViewer.title || "Payment Timeline"}</ModalHeader>
        <ModalBody>
          {timelineViewer.loading ? (
            <FullScreenLoader />
          ) : (
            <>
              <div className="admin-meta-row mb-3">
                {timelineViewer?.context?.transaction_id ? (
                  <span className="admin-chip">
                    <strong>Transaction:</strong> {timelineViewer.context.transaction_id}
                  </span>
                ) : null}
                {timelineViewer?.context?.event_group_key ? (
                  <span className="admin-chip">
                    <strong>Group:</strong> {timelineViewer.context.event_group_key}
                  </span>
                ) : null}
                {timelineViewer?.context?.flow_stage ? (
                  <span className="admin-chip admin-chip--info">
                    <strong>Stage:</strong> {timelineViewer.context.flow_stage}
                  </span>
                ) : null}
                {timelineViewer?.context?.status_label ? (
                  <span className="admin-chip admin-chip--warning">
                    <strong>Status:</strong> {timelineViewer.context.status_label}
                  </span>
                ) : null}
                {timelineViewer?.context?.timeline_source ? (
                  <span className="admin-chip">
                    <strong>Timeline Source:</strong> {getTimelineSourceLabel(timelineViewer.context.timeline_source)}
                  </span>
                ) : null}
                {timelineViewer?.context?.event_logs_degraded ? (
                  <span className="admin-chip admin-chip--danger">
                    <strong>Timeline Health:</strong> Degraded
                  </span>
                ) : null}
              </div>
              {timelineViewer?.context?.timeline_note ? (
                <div className="admin-highlight-box mb-3">{timelineViewer.context.timeline_note}</div>
              ) : null}
              {timelineViewer?.events?.length > 0 ? (
                timelineViewer.events.map((eventItem, index) => (
                  <div key={eventItem?.event_id || `${eventItem?.event_type}-${index}`} className="admin-section-card mb-2">
                    <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                      <span className={`admin-chip admin-chip--${getDirectionClass(eventItem?.direction)}`}>
                        {eventItem?.direction_label || eventItem?.direction || "Internal"}
                      </span>
                      <span className="admin-chip admin-chip--info">{eventItem?.event_type_label || eventItem?.event_type || "--"}</span>
                      {eventItem?.action_title ? <span className="admin-chip">{eventItem.action_title}</span> : null}
                      {eventItem?.status_label ? <span className="admin-chip">{eventItem.status_label}</span> : null}
                    </div>
                    <div className="admin-inline-note">
                      {formatDateTime(eventItem?.created_on)} {eventItem?.endpoint ? `| ${eventItem.endpoint}` : ""}
                    </div>
                    {eventItem?.action_hint ? <div className="admin-inline-note">{eventItem.action_hint}</div> : null}
                    <div className="admin-inline-note">
                      Invoice: {eventItem?.related_invoice_id || "--"} | Recurring: {eventItem?.related_recurring_id || "--"}
                    </div>
                    <div style={{ marginTop: 8, maxWidth: "100%", whiteSpace: "normal" }}>
                      {eventItem?.payload_preview || "--"}
                    </div>
                    {eventItem?.payload ? (
                      <button
                        type="button"
                        className="border-button mt-2"
                        onClick={() =>
                          openLogViewer(`${eventItem?.event_type_label || eventItem?.event_type || "Timeline Event"} (Full)`, eventItem.payload)
                        }
                      >
                        <span>View Full Payload</span>
                      </button>
                    ) : null}
                  </div>
                ))
              ) : (
                <div className="admin-empty-state">{timelineEmptyMessage}</div>
              )}
            </>
          )}
        </ModalBody>
      </Modal>

      <Modal isOpen={logViewer.isOpen} toggle={closeLogViewer} size="lg" centered>
        <ModalHeader toggle={closeLogViewer}>{logViewer.title}</ModalHeader>
        <ModalBody>
          <pre
            style={{
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              maxHeight: "70vh",
              overflowY: "auto",
              margin: 0,
              background: "#f8f9fb",
              border: "1px solid #ececec",
              borderRadius: 8,
              padding: 12,
            }}
          >
            {logViewer.content}
          </pre>
        </ModalBody>
      </Modal>
    </Card>
  );
};

export default PaymentLogsReport;
